import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { User, Bell, Shield, Palette, Moon, Sun, Save, Check, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { cn } from '../lib/utils';

// Color scheme definitions with visual colors
const COLOR_OPTIONS = [
  { id: 'emerald', name: 'Emerald', color: '#10b981' },
  { id: 'blue', name: 'Blue', color: '#3b82f6' },
  { id: 'purple', name: 'Purple', color: '#8b5cf6' },
  { id: 'orange', name: 'Orange', color: '#f97316' },
];

const SettingsPage = () => {
  const { user } = useAuth();
  const { theme, toggleTheme, colorScheme, setColorScheme } = useTheme();
  const [saved, setSaved] = useState(false);
  const [notifications, setNotifications] = useState({
    email: true,
    push: false,
    marketing: false
  });

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="h-full bg-zinc-950 overflow-auto">
      <div className="max-w-4xl mx-auto p-6">
        <h1 className="text-2xl font-bold text-white mb-6">Settings</h1>

        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="bg-zinc-900 border border-zinc-800">
            <TabsTrigger value="profile" className="data-[state=active]:bg-zinc-800">
              <User className="h-4 w-4 mr-2" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="appearance" className="data-[state=active]:bg-zinc-800">
              <Palette className="h-4 w-4 mr-2" />
              Appearance
            </TabsTrigger>
            <TabsTrigger value="notifications" className="data-[state=active]:bg-zinc-800">
              <Bell className="h-4 w-4 mr-2" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="security" className="data-[state=active]:bg-zinc-800">
              <Shield className="h-4 w-4 mr-2" />
              Security
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white">Profile Information</CardTitle>
                <CardDescription className="text-zinc-400">
                  Update your profile details and public information.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center gap-6">
                  <Avatar className="h-20 w-20">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-zinc-700 text-white text-xl">
                      {user?.name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                      Change Avatar
                    </Button>
                    <p className="text-xs text-zinc-500 mt-2">JPG, PNG or GIF. Max 2MB.</p>
                  </div>
                </div>

                <div className="grid gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="name" className="text-zinc-300">Display Name</Label>
                    <Input
                      id="name"
                      defaultValue={user?.name}
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="email" className="text-zinc-300">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      defaultValue={user?.email}
                      disabled
                      className="bg-zinc-800 border-zinc-700 text-zinc-400"
                    />
                    <p className="text-xs text-zinc-500">Email is managed by your account settings.</p>
                  </div>
                </div>

                <Button onClick={handleSave} className="bg-emerald-600 hover:bg-emerald-500">
                  {saved ? (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Saved!
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Appearance Tab */}
          <TabsContent value="appearance">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white">Appearance</CardTitle>
                <CardDescription className="text-zinc-400">
                  Customize how JAF AI looks on your device.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className="text-zinc-300">Theme</Label>
                    <p className="text-sm text-zinc-500">Switch between light and dark mode.</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Sun className="h-4 w-4 text-zinc-400" />
                    <Switch
                      checked={theme === 'dark'}
                      onCheckedChange={toggleTheme}
                    />
                    <Moon className="h-4 w-4 text-zinc-400" />
                  </div>
                </div>

                <div className="pt-4 border-t border-zinc-800">
                  <Label className="text-zinc-300 block mb-3">Color Scheme</Label>
                  <div className="flex gap-3">
                    {COLOR_OPTIONS.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => setColorScheme(option.id)}
                        className={cn(
                          "w-10 h-10 rounded-full ring-2 ring-offset-2 ring-offset-zinc-900 transition-all relative",
                          colorScheme === option.id ? 'ring-white scale-110' : 'ring-transparent hover:ring-zinc-600'
                        )}
                        style={{ backgroundColor: option.color }}
                        title={option.name}
                      >
                        {colorScheme === option.id && (
                          <CheckCircle2 className="absolute inset-0 m-auto h-5 w-5 text-white drop-shadow-lg" />
                        )}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-zinc-500 mt-2">Current: {COLOR_OPTIONS.find(c => c.id === colorScheme)?.name || 'Emerald'}</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white">Notification Preferences</CardTitle>
                <CardDescription className="text-zinc-400">
                  Manage how you receive notifications.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className="text-zinc-300">Email Notifications</Label>
                    <p className="text-sm text-zinc-500">Receive updates via email.</p>
                  </div>
                  <Switch
                    checked={notifications.email}
                    onCheckedChange={(checked) => setNotifications({...notifications, email: checked})}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className="text-zinc-300">Push Notifications</Label>
                    <p className="text-sm text-zinc-500">Receive push notifications in browser.</p>
                  </div>
                  <Switch
                    checked={notifications.push}
                    onCheckedChange={(checked) => setNotifications({...notifications, push: checked})}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className="text-zinc-300">Marketing Emails</Label>
                    <p className="text-sm text-zinc-500">Receive news and product updates.</p>
                  </div>
                  <Switch
                    checked={notifications.marketing}
                    onCheckedChange={(checked) => setNotifications({...notifications, marketing: checked})}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white">Security Settings</CardTitle>
                <CardDescription className="text-zinc-400">
                  Manage your account security and sessions.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-4 bg-zinc-800 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <Shield className="h-5 w-5 text-emerald-400" />
                    </div>
                    <div>
                      <p className="font-medium text-white">Secure Account</p>
                      <p className="text-sm text-zinc-400">Your account is protected with secure authentication.</p>
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-zinc-300 block mb-3">Active Sessions</Label>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                      <div>
                        <p className="text-sm text-white">Current Session</p>
                        <p className="text-xs text-zinc-500">Last active: Just now</p>
                      </div>
                      <span className="text-xs text-emerald-400 bg-emerald-400/10 px-2 py-1 rounded">Active</span>
                    </div>
                  </div>
                </div>

                <Button variant="destructive" className="bg-red-600 hover:bg-red-500">
                  Sign Out All Devices
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SettingsPage;
